import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  Font,
  StyleSheet,
} from '@react-pdf/renderer';
import { HistoricalReportData, PDFRecord } from './types';
import { getTypeColor, PDF_COLORS } from './pdfColors';
import { CoverPage } from './components/CoverPage';
import { DataGrid } from './components/DataGrid';
import { ImageGrid } from './components/ImageGrid';
import { PDFTable } from './components/PDFTable';

Font.register({
  family: 'Poppins',
  fonts: [
    { src: '/fonts/Poppins-Regular.ttf' },
    { src: '/fonts/Poppins-SemiBold.ttf', fontWeight: 600 },
  ],
});

const styles = StyleSheet.create({
  page: {
    backgroundColor: PDF_COLORS.white,
    paddingTop: 20,
    paddingBottom: 35,
    paddingHorizontal: 20,
    fontFamily: 'Poppins',
  },
  card: {
    marginBottom: 10,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: PDF_COLORS.greyMid,
    overflow: 'hidden',
  },
  cardHeader: {
    paddingVertical: 6,
    paddingHorizontal: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  roidText: {
    fontSize: 10,
    fontFamily: 'Poppins',
    fontWeight: 600,
    color: PDF_COLORS.white,
    letterSpacing: 0.3,
  },
  cardBody: {
    padding: 8,
    backgroundColor: PDF_COLORS.white,
  },
  tableTitle: {
    fontSize: 8,
    fontFamily: 'Poppins',
    fontWeight: 600,
    color: PDF_COLORS.ink,
    marginTop: 8,
  },
  footer: {
    position: 'absolute',
    bottom: 12,
    left: 20,
    right: 20,
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderTopWidth: 0.5,
    borderTopColor: PDF_COLORS.greyMid,
    paddingTop: 3,
  },
  footerText: {
    fontSize: 7,
    fontFamily: 'Poppins',
    color: PDF_COLORS.greyDark,
  },
  footerBrand: {
    flex: 1,
    textAlign: 'center',
  },
});

interface RecordCardProps {
  record: PDFRecord;
  typeColor: string;
}

const RecordCard = ({ record, typeColor }: RecordCardProps) => (
  // Si el record trae tabla (detalle de OTs), la tarjeta DEBE poder fluir
  // entre páginas. OJO react-pdf: comprueba `'wrap' in props`, así que pasar
  // wrap={undefined} cuenta como "wrap desactivado" (≠ omitir el prop) — la
  // tarjeta no partía nunca y yoga la comprimía en una página solapando las
  // filas. Hay que pasar un boolean real.
  <View style={styles.card} wrap={!!(record.table || record.tables?.length)}>
    <View style={[styles.cardHeader, { backgroundColor: typeColor }]}>
      <Text style={styles.roidText}>{record.roid}</Text>
    </View>
    <View style={styles.cardBody}>
      <DataGrid fields={record.fields} />
      {record.table && <PDFTable table={record.table} />}
      {record.tables?.map((titled, i) => (
        <View key={i}>
          {titled.title && (
            <Text style={styles.tableTitle}>{titled.title}</Text>
          )}
          <PDFTable table={titled.table} />
        </View>
      ))}
      {record.images.length > 0 && <ImageGrid images={record.images} />}
    </View>
  </View>
);

interface HistoricalReportProps {
  data: HistoricalReportData;
}

export const HistoricalReport = ({ data }: HistoricalReportProps) => {
  const typeColor = getTypeColor(data.orderType);

  return (
    <Document>
      <CoverPage
        title={data.title}
        typeColor={typeColor}
        generatedAt={data.generatedAt}
        totalCount={data.totalCount}
        filtersSummary={data.filtersSummary}
      />
      <Page size="A4" style={styles.page}>
        {data.records.map((record, i) => (
          <RecordCard key={i} record={record} typeColor={typeColor} />
        ))}
        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>{data.title}</Text>
          <Text style={[styles.footerText, styles.footerBrand]}>
            UTE Eix Transversal C-25 · Generalitat de Catalunya
          </Text>
          {/* Sin «página X / Y»: retirado a petición del cliente
              (2026-08-04). El hueco se conserva para que la marca siga
              centrada en el pie. */}
          <Text style={styles.footerText} />
        </View>
      </Page>
    </Document>
  );
};
